import { Icon } from "../icons/Icon"
import { Image } from "../ui/Image"
import { useEffect, useMemo, useState } from "react"
import { Pair, StylePreview } from "./StylePreviewPanel"
import { ImageFormat, processImageUrl } from "@/utils/process-image-url"
import { useTranslation } from "react-i18next"

const toMasonryUrl = (url: string | null) =>
	url ? processImageUrl({ url, format: ImageFormat.AVIF }) : null

export interface StylePreviewCellProps {
	photoId?: string | number
	source?: Blob | string | null
	animateWhileLoading?: boolean
}

export const StylePreviewCell = (props: StylePreviewCellProps) => {
	const { t } = useTranslation("components")
	const { photoId, source, animateWhileLoading } = props

	const [sourceUrl, setSourceUrl] = useState<string | undefined>(undefined)

	useEffect(() => {
		if (!source) {
			return
		}

		if (typeof source === "string") {
			setSourceUrl(source)
			return
		}

		const blob = source
		const u = URL.createObjectURL(blob)
		setSourceUrl(u)

		return () => URL.revokeObjectURL(u)
	}, [source])

	return (
		<div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg bg-base-700 transition-transform duration-200">
			{sourceUrl ? (
				<Image
					animateWhileLoading={animateWhileLoading}
					className="size-full object-cover"
					src={sourceUrl}
					alt={t("stylePreviewCell.beforePhoto", { var0: photoId ?? "" })} // i18n-ignore: generic preview alt, not user-visible text
				/>
			) : (
				<Icon name="photo" size={24} className="text-base-500" />
			)}
		</div>
	)
}

export interface ComparableStylePreviewCellProps extends StylePreviewCellProps {
	placeholder?: Blob | string | null
	altText?: string
	index: number
	param: Pair<StylePreview>
	onPreviewItemClick: (pair: Pair<StylePreview>, index: number) => void
}

export const ComparableStylePreviewCell = (props: ComparableStylePreviewCellProps) => {
	const { altText, index, source, placeholder, animateWhileLoading, param, onPreviewItemClick } =
		props

	const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined)

	useEffect(() => {
		const imgSource = source ?? placeholder
		if (!imgSource || typeof imgSource === "string") {
			return
		}
		const u = URL.createObjectURL(imgSource)
		setBlobUrl(u)
		return () => URL.revokeObjectURL(u)
	}, [source, placeholder])

	const beforeUrl = useMemo(() => toMasonryUrl(param.before.url), [param.before.url])
	const afterUrl = useMemo(() => toMasonryUrl(param.after.url), [param.after.url])

	const hasCatalogUrls = Boolean(beforeUrl || afterUrl)
	const showBefore = typeof source === "string" && source === param.before.url

	return (
		<div
			className="cursor-pointer overflow-hidden bg-base-600 transition-opacity"
			onClick={() => onPreviewItemClick(param, index)}
		>
			{hasCatalogUrls ? (
				<>
					{beforeUrl && (
						<Image
							animateWhileLoading={animateWhileLoading}
							className="w-full object-cover"
							src={beforeUrl}
							alt={altText}
							style={{ height: "auto", display: showBefore ? undefined : "none" }}
						/>
					)}
					{afterUrl && (
						<Image
							animateWhileLoading={animateWhileLoading}
							className="w-full object-cover"
							src={afterUrl}
							alt={altText}
							style={{ height: "auto", display: showBefore ? "none" : undefined }}
						/>
					)}
				</>
			) : blobUrl ? (
				<Image
					animateWhileLoading={animateWhileLoading}
					className="w-full object-cover"
					src={blobUrl}
					alt={altText}
					style={{ height: "auto" }}
				/>
			) : (
				<div className="flex aspect-[16/9] items-center justify-center">
					<Icon name="photo" size={24} className="text-base-500" />
				</div>
			)}
		</div>
	)
}
